import { useDispatch, useSelector } from "react-redux";
import { PageTitle } from "_metronic/layout/core";
import * as Yup from "yup";
import { Form, Formik, FormikHelpers } from "formik";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { AddAccountFormData, AddAccountFormSteps } from "./components/steps/StepModel";
import { AddAccountStep } from "./components/AddAccountStep";
import { selectCurrentStep, selectLoading, selectPreviousStep, setCurrentStep } from "./components/AddAccountSlice";
import { ClientAccountNumberSubmit } from "./components/steps/ClientAccountNumberStep";
import { EmailAddressSubmit } from "./components/steps/EmailAddressStep";
import { EmailVerificationCodeSubmit } from "./components/steps/EmailVerificationCodeStep";
import { SuccessSubmit } from "./components/steps/SuccessStep";
import translations from "utils/translationUtility";
import AppBackButton from "app/modules/shared/appBackButton/AppBackButton";

const initialValues: AddAccountFormData = {
  clientAccountNumber: "",
  emailAddress: "",
  emailVerificationCode: "",
};

const validationSchema = {
  [AddAccountFormSteps.ClientAccountNumber]: Yup.object().shape({
    clientAccountNumber: Yup.string().required(`${translations.client} account number is required`),
  }),
  [AddAccountFormSteps.EmailAddress]: Yup.object().shape({
    emailAddress: Yup.string().email("Wrong email format").required("Email address is required"),
  }),
  [AddAccountFormSteps.EmailVerificationCode]: Yup.object().shape({
    emailVerificationCode: Yup.string()
      .min(6, "Minimum 6 symbols")
      .max(6, "Maximum 6 symbols")
      .required("Email verification code is required"),
  }),
};

const submitFunctions = {
  [AddAccountFormSteps.ClientAccountNumber]: ClientAccountNumberSubmit,
  [AddAccountFormSteps.EmailAddress]: EmailAddressSubmit,
  [AddAccountFormSteps.EmailVerificationCode]: EmailVerificationCodeSubmit,
  [AddAccountFormSteps.Success]: SuccessSubmit,
};

const AddAccount = () => {
  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();

  const currentStep = useSelector(selectCurrentStep);
  const previousStep = useSelector(selectPreviousStep);
  const loading = useSelector(selectLoading);

  const getCardHeader = () => {
    switch (currentStep) {
      case AddAccountFormSteps.ClientAccountNumber:
        return `Please enter the ${translations.client.toLocaleLowerCase()} account number you would like to connect to`;
      case AddAccountFormSteps.EmailAddress:
        return `Please enter the email address for the ${translations.client.toLocaleLowerCase()} account`;
      case AddAccountFormSteps.EmailVerificationCode:
        return "Please enter the verification code sent to the email address";
      case AddAccountFormSteps.Success:
        return "Success!";
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema[currentStep]}
        onSubmit={(values: AddAccountFormData, helpers: FormikHelpers<AddAccountFormData>) => {
          submitFunctions[currentStep]({ values, dispatch, thunkDispatch, helpers });
          helpers.setTouched({});
        }}
      >
        {(formikProps) => (
          <Form>
            <PageTitle>Add Account</PageTitle>
            <AppBackButton backTo="/accounts" data-test-id="add-account__back-button" />
            <div className="card mb-5 mb-xl-10">
              <div className="card-header border-0 cursor-pointer" role="button">
                <div className="card-title m-0">
                  <h3 className="fw-bolder m-0">{getCardHeader()}</h3>
                </div>
              </div>

              <div id="kt_account_profile_details" className="show">
                <div className="card-body border-top p-9">
                  <div className="row mb-6">
                    <AddAccountStep currentStep={currentStep} formikProps={formikProps} />
                  </div>
                </div>
                {/* begin::Action */}
                {currentStep !== AddAccountFormSteps.Success && (
                  <div className="card-footer d-flex justify-content-end py-6 px-9 gap-2">
                    {previousStep != null && (
                      <button
                        type="button"
                        className="cpl-btn cpl-btn-primary"
                        disabled={loading}
                        onClick={() => dispatch(setCurrentStep(previousStep))}
                        data-test-id="add-account-form__previous-step-button"
                      >
                        Back
                      </button>
                    )}
                    <button
                      type="submit"
                      className="cpl-btn cpl-btn-primary"
                      disabled={loading}
                      data-test-id="add-account-form__continue-button"
                    >
                      {!loading && "Continue"}
                      {loading && (
                        <span className="indicator-progress" style={{ display: "block" }}>
                          Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                )}
                {/* end::Action */}
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export { AddAccount };
